import { useCallback, useEffect, useRef, useState } from "react";
import {
  ASSISTANT_UPDATED,
  createConversation,
  deleteConversation as apiDelete,
  getMessages,
  listConversations,
  notifyAssistantUpdated,
  renameConversation as apiRename,
  sendMessage,
} from "./assistantApi";

/**
 * Conversation list + active thread + sending.
 *
 * Shared by the full-page assistant and the floating drawer so both see the
 * same conversations. `autoSelectLatest` is what the drawer uses to continue
 * wherever the user left off instead of starting a new thread each time.
 */
export default function useAssistant({ autoSelectLatest = true } = {}) {
  const [conversations, setConversations] = useState([]);
  const [activeUid, setActiveUid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const mounted = useRef(true);
  const localMediaUrls = useRef(new Set());
  // Custom events are delivered to the sender too. Keep an identity token so
  // this hook ignores its own notification while other assistant surfaces can
  // still use it to synchronize.
  const updateSource = useRef({});
  // Re-arm on every mount: StrictMode runs mount/unmount/mount in dev, so a
  // cleanup-only effect would leave this false for the rest of the session and
  // every `if (mounted.current)` guard below would silently skip its setState.
  useEffect(() => {
    mounted.current = true;
    const urls = localMediaUrls.current;
    return () => {
      mounted.current = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const list = await listConversations();
      if (!mounted.current) return list;
      setConversations(list);
      return list;
    } catch (e) {
      if (mounted.current) setError("list");
      return [];
    } finally {
      if (mounted.current) setLoadingList(false);
    }
  }, []);

  const openConversation = useCallback(async (uid) => {
    setActiveUid(uid);
    setMessages([]);
    if (!uid) return;
    setLoadingThread(true);
    try {
      const data = await getMessages(uid);
      if (mounted.current) setMessages(data.messages || []);
    } catch (e) {
      if (mounted.current) setError("thread");
    } finally {
      if (mounted.current) setLoadingThread(false);
    }
  }, []);

  // Initial load: pick up the most recent thread so the drawer and the page
  // continue the same conversation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await refreshList();
      if (cancelled || !autoSelectLatest) return;
      if (list.length) openConversation(list[0].uid);
    })();
    return () => { cancelled = true; };
  }, [refreshList, openConversation, autoSelectLatest]);

  // Refetch when the other surface sends something.
  useEffect(() => {
    const onUpdated = (e) => {
      if (e?.detail?.source === updateSource.current) return;
      refreshList();
      const uid = e?.detail?.conversationUid;
      if (uid && uid === activeUid) openConversation(uid);
    };
    window.addEventListener(ASSISTANT_UPDATED, onUpdated);
    return () => window.removeEventListener(ASSISTANT_UPDATED, onUpdated);
  }, [refreshList, openConversation, activeUid]);

  const startNew = useCallback(async () => {
    setActiveUid(null);
    setMessages([]);
    setError(null);
  }, []);

  const send = useCallback(
    async ({ text, image, countingJobId, transcriptionIds }) => {
      if (!text && !image && !countingJobId) return;
      setError(null);
      setErrorCode(null);
      setSending(true);

      // Optimistic user bubble so the UI responds immediately.
      const optimistic = {
        messageId: `tmp-${Date.now()}`,
        sender: "User",
        messageType: image ? (image.type.startsWith("video/") ? "file" : "image") : "text",
        content: text || "",
        localImage: image ? URL.createObjectURL(image) : null,
        createdAt: new Date().toISOString(),
      };
      if (optimistic.localImage) localMediaUrls.current.add(optimistic.localImage);
      setMessages((prev) => [...prev, optimistic]);

      try {
        const res = await sendMessage({ uid: activeUid, text, image, countingJobId, ...(transcriptionIds?.length ? { transcriptionIds } : {}) });
        const uid = res?.conversation?.uid || activeUid;
        if (!mounted.current) return res;

        // Replace the optimistic blob with the persisted server row. This is
        // what makes an uploaded image immediately identical in the page, the
        // floating drawer and the next browser/mobile session.
        let synchronized = false;
        if (uid) {
          try {
            const thread = await getMessages(uid);
            if (mounted.current) {
              setMessages(thread.messages || []);
              synchronized = true;
            }
          } catch (syncError) {
            // Keep the optimistic turn below if the follow-up GET is unavailable.
          }
        }
        if (!synchronized) {
          setMessages((prev) => [
            ...prev,
            {
              messageId: res.messageId,
              sender: "Bot",
              messageType: "text",
              content: res.analysis,
              toolTrace: res.toolTrace ? JSON.stringify(res.toolTrace) : null,
              createdAt: new Date().toISOString(),
            },
          ]);
        } else if (optimistic.localImage) {
          URL.revokeObjectURL(optimistic.localImage);
          localMediaUrls.current.delete(optimistic.localImage);
        }

        if (uid && uid !== activeUid) setActiveUid(uid);
        refreshList();
        notifyAssistantUpdated(uid, updateSource.current);
        return res;
      } catch (e) {
        if (!mounted.current) throw e;
        setError("send");
        setErrorCode(e.response?.data?.message || e.code || null);

        // The turn may have been stored even though we lost the response — a
        // backend restart mid-request does exactly that. Re-sync from the
        // server so a reply the user already waited for is not thrown away,
        // and only drop the optimistic bubble if nothing was persisted.
        try {
          const list = await refreshList();
          const uid = activeUid || (list[0] && list[0].uid);
          if (uid) await openConversation(uid);
          else
            setMessages((prev) =>
              prev.filter((m) => m.messageId !== optimistic.messageId)
            );
        } catch (resyncError) {
          setMessages((prev) =>
            prev.filter((m) => m.messageId !== optimistic.messageId)
          );
        }
        if (optimistic.localImage) {
          URL.revokeObjectURL(optimistic.localImage);
          localMediaUrls.current.delete(optimistic.localImage);
        }
        throw e;
      } finally {
        if (mounted.current) setSending(false);
      }
    },
    [activeUid, refreshList, openConversation]
  );

  const rename = useCallback(async (uid, title) => {
    await apiRename(uid, title);
    refreshList();
  }, [refreshList]);

  const remove = useCallback(async (uid) => {
    await apiDelete(uid);
    const list = await refreshList();
    if (uid === activeUid) {
      if (list.length) openConversation(list[0].uid);
      else { setActiveUid(null); setMessages([]); }
    }
  }, [activeUid, refreshList, openConversation]);

  const createAndOpen = useCallback(async () => {
    const conv = await createConversation(null);
    await refreshList();
    openConversation(conv.uid);
    return conv;
  }, [refreshList, openConversation]);

  return {
    conversations, activeUid, messages,
    loadingList, loadingThread, sending, error, errorCode,
    openConversation, startNew, createAndOpen, send, rename, remove, refreshList,
  };
}
